import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { ConfigService } from '@/services/config.service';
import { Config } from '@/models/config';
import { FacilityService } from '@/services/facility.service';
import { SurveysService } from '@/services/surveys.service';
import { Facility } from '@/models/facility';
import { Language } from '@/models/language';
import { Progress } from '@/models/survey';

const DEFAULT_LANG = { id: 1, code: 'pl' };

Vue.use(Vuex);
interface RootStore {
  surveyId: string | null;
  isLoading: boolean;
  config: Config | null;
  reviewId: string | null;
  writeReviewUrl: string | null;
  defaultFacility: Facility | null;
  reviewText: string | null;
  activeViewName: string | null;
  activeBranchIdentifier: string;
  isFullscreenDropdown: boolean;
  isNextView: boolean;
  isAnswerInProgress: boolean;
  originLink: string | null;
  progress: Progress | null;
}

const store: StoreOptions<RootStore> = {
  state: {
    isLoading: false,
    config: null,
    writeReviewUrl: null,
    reviewId: null,
    defaultFacility: null,
    surveyId: null,
    reviewText: null,
    isFullscreenDropdown: false,
    activeViewName: null,
    activeBranchIdentifier: '',
    isNextView: false,
    isAnswerInProgress: false,
    originLink: null,
    progress: null,
  },
  mutations: {
    SET_IS_NEXT_VIEW(state, payload) {
      state.isNextView = payload;
    },
    SET_ACTIVE_VIEW_NAME(state, payload) {
      state.activeViewName = payload;
    },
    SET_ACTIVE_BRANCH_IDENTIFIER(state, payload) {
      state.activeBranchIdentifier = payload;
    },
    SET_REVIEW_TEXT(state, payload) {
      state.reviewText = payload;
    },
    SET_SURVEY_ID(state, payload) {
      state.surveyId = payload;
    },
    SET_CONFIG(state, payload) {
      state.config = payload;
    },
    SET_IS_LOADING(state, payload) {
      state.isLoading = payload;
    },
    SET_WRITE_REVIEW_URL(state, payload) {
      state.writeReviewUrl = payload;
    },
    SET_REVIEW_ID(state, payload) {
      state.reviewId = payload;
    },
    SET_DEFAULT_FACILITY(state, payload) {
      state.defaultFacility = payload;
    },
    SET_IS_FULLSCREEN_DROPDOWN(state, payload) {
      state.isFullscreenDropdown = payload;
    },
    SET_IS_ANSWER_IN_PROGRESS(state, payload) {
      state.isAnswerInProgress = payload;
    },
    SET_ORIGIN_LINK(state, payload) {
      state.originLink = payload;
    },
    SET_PROGRESS(state, payload) {
      state.progress = payload;
    },
  },
  actions: {
    setProgress({ commit }, progress: Progress) {
      commit('SET_PROGRESS', progress);
    },
    setIsAnswerInProgress({ commit }, isAnswerInProgress: boolean) {
      commit('SET_IS_ANSWER_IN_PROGRESS', isAnswerInProgress);
    },
    setIsNextView({ commit }, isNextView: boolean) {
      commit('SET_IS_NEXT_VIEW', isNextView);
    },
    setActiveViewName({ commit }, name: string) {
      commit('SET_ACTIVE_VIEW_NAME', name);
    },
    setActiveBranchIdentifier({ commit }, identifier: string) {
      commit('SET_ACTIVE_BRANCH_IDENTIFIER', identifier);
    },
    setIsFullscreenDropdown({ commit }, isFullscreen: string) {
      commit('SET_IS_FULLSCREEN_DROPDOWN', isFullscreen);
    },
    setReviewText({ commit }, text: string | null) {
      commit('SET_REVIEW_TEXT', text);
    },
    setSurveyId({ commit }, surveyId: string) {
      commit('SET_SURVEY_ID', surveyId);
    },
    setWriteReviewUrl({ commit }, url: string) {
      commit('SET_WRITE_REVIEW_URL', url);
    },
    setIsLoading({ commit }, isLoading: boolean) {
      commit('SET_IS_LOADING', isLoading);
    },
    setReviewId({ commit }, id: string) {
      commit('SET_REVIEW_ID', id);
    },
    async setConfig({ commit }, slug: string) {
      const data = await ConfigService.getConfig(slug);
      commit('SET_CONFIG', data);
    },
    async setDefaultFacility({ commit }, facility: Facility) {
      commit('SET_DEFAULT_FACILITY', facility);
    },
    async setDefaultFacilityById({ commit }, payload) {
      const facility = await FacilityService.getFacilityById(payload);
      commit('SET_DEFAULT_FACILITY', facility);
    },
    async setDefaultFacilityByStoreCode({ commit }, payload) {
      const facility = await FacilityService.getFacilities({
        store_code: payload,
      });

      if (!facility.results.length) {
        throw new Error();
      }

      commit('SET_DEFAULT_FACILITY', facility.results[0]);
    },
    async setTranslations({ commit, state, getters }) {
      const translations = await SurveysService.getTranslations(
        getters.defaultLanguage.code
      );
      commit('SET_TRANSLATIONS', translations);
    },
    setOriginLink({ commit }, originLink: string) {
      commit('SET_ORIGIN_LINK', originLink);
    },
  },
  getters: {
    token(state): string | undefined {
      return state.config?.key;
    },
    privacyPolicyUrl(state): boolean | undefined {
      return state.config?.privacyPolicyFileLink;
    },
    shouldAcquireEmail(state): boolean | undefined {
      return state.config?.shouldAcquireEmail;
    },
    shouldAcquirePhoneNumber(state): boolean | undefined {
      return state.config?.shouldAcquirePhoneNumber;
    },
    reviewTitle(state): string | undefined {
      return state.config?.reviewRequestHeader;
    },
    negativeReviewPromptHeader(state): string | undefined {
      return state.config?.negativeReviewPromptHeader;
    },
    negativeReviewPromptBody(state): string | undefined {
      return state.config?.negativeReviewPromptBody;
    },
    reviewSubtitle(state): string | undefined {
      return state.config?.reviewRequestSubheader;
    },
    reviewDescription(state): string | undefined {
      return state.config?.reviewRequestBody;
    },
    negativeReviewTitle(state): string | undefined {
      return state.config?.negativeReviewHeader;
    },
    negativeReviewDescription(state): string | undefined {
      return state.config?.negativeReviewBody;
    },
    positiveReviewTitle(state): string | undefined {
      return state.config?.positiveReviewHeader;
    },
    positiveReviewDescription(state): string | undefined {
      return state.config?.positiveReviewBody;
    },
    positiveRatingStart(state): number | undefined {
      return state.config?.positiveRatingStart;
    },

    colorsTheme(state): {
      bg: string | undefined;
      primary: string | undefined;
    } {
      return { bg: state.config?.bgColor, primary: state.config?.primaryColor };
    },

    logo(state): string | null | undefined {
      return state.config?.logo;
    },
    slug(state): string | undefined {
      return state.config?.slug;
    },
    defaultLanguage(state): Language {
      return state.config?.defaultLanguage || DEFAULT_LANG;
    },
    isVox(state): boolean {
      const keys = [
        '656f5174-900b-4f86-b0c8-43dd51dc7a49',
        'c1c1f8af-13f4-439e-a7bd-73537bddba2a',
        '4fc0c3c6-0a17-4f61-9348-915050f674d2',
      ];
      return state.config?.key ? keys.includes(state.config?.key) : false;
    },
  },
};

export default new Vuex.Store<RootStore>(store);
